import { fetchLinks } from '@/fetchers/fetchLinks'
import { useIframeSide } from '@/hooks/useIframe'
import { useSideBySideSupport } from '@/hooks/useSideBySideSupport'
import CustomModal from '@/legacy-ported/components/base/CustomModal'
import VideoModal, {
	useVideoModal,
} from '@/legacy-ported/components/base/VideoModal'
import { ILink } from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { Alert } from 'nsw-ds-react'
import { ReactNode, useMemo, useState } from 'react'
import { Loading } from '../Loading'
import RichText from '../RichText'
import { useKontentHomeConfig } from '../contexts/KontentHomeConfigProvider'
import { useWebsiteConfiguration } from '../contexts/WebsiteConfigurationProvider'
import { LinkPropsExtended } from './RichtextLinkWrapper'

interface RichtextLinkWeblinkCommonProps {
	link: ILink
	children: (_: LinkPropsExtended) => ReactNode
	preview?: boolean
}

export const RichtextLinkWeblinkCommon = ({
	link,
	children,
	preview,
}: RichtextLinkWeblinkCommonProps) => {
	const router = useRouter()

	const websiteConfiguration = useWebsiteConfiguration()
	const { isInIframe, mappings } = useKontentHomeConfig()
	const iframeSide = useIframeSide()
	const isViewSideBySideSupported = useSideBySideSupport()
	// video modal
	const { currentVideoIframeUrl, openVideoModal, openVideo, hideVideo } =
		useVideoModal()
	const [showPopup, setShowPopup] = useState(false)

	const { data, isFetched } = useQuery({
		queryKey: ['RichtextLinkWeblinkCommon', link.codename, preview],
		queryFn: () => {
			return fetchLinks({
				codenames: [link.codename],
				preview,
			})
		},
		staleTime: Infinity,
	})

	const isInSyllabusTabs = useMemo(() => {
		return router.pathname.includes(
			'/learning-areas/[learningarea]/[syllabus]',
		)
	}, [router.pathname])

	if (!isFetched) {
		return <Loading />
	}

	if (isFetched && !data) {
		return null
	}

	// Find the link in the data
	const theLink = data?.json?.data?.[0]
	if (!theLink?.url) {
		return null
	}

	if (link.type === 'weblinkvideo') {
		// If the link is a video, we need to return a different component
		return (
			<>
				{children({
					href: theLink.url,
					scroll: true,
					onClick: (ev) => {
						ev.preventDefault()
						openVideo(theLink.url, theLink.title)
					},
				})}
				{openVideoModal && currentVideoIframeUrl && (
					<VideoModal
						ariaLabel={theLink.title}
						modalStatus={openVideoModal}
						onCancel={hideVideo}
						video={currentVideoIframeUrl}
					/>
				)}
			</>
		)
	}

	if (link.type === 'weblinkint' && isInIframe) {
		return (
			<>
				{children({
					href: theLink.url,
					scroll: true,
					onClick: (ev) => {
						ev.preventDefault()
						setShowPopup(true)
					},
				})}
				{showPopup && (
					<CustomModal
						title="Information"
						modalStatus={showPopup}
						handleCancel={() => setShowPopup(false)}
						confirmButtonText="Open in new tab"
						handleConfirm={() => {
							setShowPopup(false)
							window.open(theLink.url, '_blank')
						}}
					>
						<Alert as="info">
							<RichText
								richTextElement={
									websiteConfiguration.item.elements
										.message_splitview_linkblock
								}
								linkedItems={websiteConfiguration.linkedItems}
								mappings={mappings}
							></RichText>
						</Alert>
					</CustomModal>
				)}
			</>
		)
	}

	return (
		<>
			{children({
				href: theLink.url,
				scroll: true,
				target: link.type === 'weblinkext' ? '_blank' : undefined,
			})}
			{link.type !== 'weblinkext' &&
				theLink.enable_compare_view &&
				isInSyllabusTabs &&
				isViewSideBySideSupported && (
					<span className="inline-flex ml-[10px] align-sub">
						<div className="w-[1px] h-[24px] bg-gray-400 box-content" />
						<button
							onClick={() => {
								// if it's inside the iframe, I want to post message to the parent
								// to open the right side
								if (isInIframe) {
									if (iframeSide) {
										window.parent.postMessage(
											{
												type: `${
													iframeSide === 'left'
														? 'right'
														: 'left'
												}UrlChange`,
												url: theLink.url,
											},
											'*',
										)
									}
								} else {
									// left url is current url without protocol and host, just the pathname, query string and hash
									const leftUrl =
										window.location.pathname +
										window.location.search +
										window.location.hash

									// left is current page, right is the target
									window.location.href = `/view-side-by-side?l=${encodeURIComponent(
										leftUrl,
									)}&r=${encodeURIComponent(theLink.url)}`
								}
							}}
							aria-label="Open in View side-by-side"
							className={clsx(
								'inline-block ml-1 text-nsw-brand-dark w-8',
							)}
							onFocus={(e) => {
								e.target.style.outline = 'none'
								e.target.style.boxShadow =
									'0 0 0 3px var(--nsw-focus)'
							}}
							onBlur={(e) => {
								e.target.style.boxShadow = 'none'
							}}
						>
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H9V6H4V18H9V20H4ZM11 22V2H13V4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H13V22H11ZM13 18H20V6H13V18Z"
									fill="currentColor"
								/>
							</svg>
						</button>
					</span>
				)}
		</>
	)
}
